/**
 * AdminStorageConsole — Super Admin Storage Console.
 *
 * On-demand view of Tigris Object Storage usage: total used against the 5 GB
 * quota (as a coloured progress bar) plus a per-module breakdown (files +
 * size). Does NOT auto-refresh — the Super Admin taps "Refresh" to re-query.
 * Data comes from GET /api/system/storage-usage (super-admin only).
 */
import React, { useCallback, useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { HardDrive, RefreshCw, AlertTriangle } from 'lucide-react';

// Adaptive byte formatter so tiny modules read as "75 B", not "0.00 MB".
function formatBytes(n) {
  const b = Number(n) || 0;
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(2)} GB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(2)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(2)} KB`;
  return `${b} B`;
}

// Average object size for a module — always expressed in KB (size ÷ files).
// Returns "—" when the module has no files so we never divide by zero.
function formatAvgKB(bytes, files) {
  const f = Number(files) || 0;
  if (f <= 0) return '—';
  const kb = (Number(bytes) || 0) / f / 1024;
  return `${kb.toFixed(2)} KB`;
}

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
}

// Quota bar colour by fill level — reassuring green low, amber mid, rose high.
function usageColor(pct) {
  if (pct >= 90) return { bar: 'bg-rose-500', text: 'text-rose-600', soft: 'bg-rose-100' };
  if (pct >= 75) return { bar: 'bg-amber-500', text: 'text-amber-600', soft: 'bg-amber-100' };
  return { bar: 'bg-emerald-500', text: 'text-emerald-600', soft: 'bg-emerald-100' };
}

export default function AdminStorageConsole() {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return (
    <AppShell
      title="Storage Console"
      subtitle="Object storage (Tigris) usage across the portal. Tap Refresh to re-query — this view does not auto-update."
    >
      <StorageConsoleContent />
    </AppShell>
  );
}

function StorageConsoleContent() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const load = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true); else setLoading(true);
    try {
      const { data: res } = await api.get('/system/storage-usage');
      setData(res);
      setError('');
      if (isManual) toast.success('Storage usage refreshed');
    } catch (e) {
      const msg = extractError(e, 'Failed to load storage usage');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, []);

  const pct = data ? Math.min(100, Number(data.percent_used) || 0) : 0;
  const colors = usageColor(pct);
  const totalBytes = data?.total_bytes || 0;
  // Today's activity, grouped by the same modules as the breakdown (non-zero only).
  const todayModules = data ? data.modules.filter((m) => (m.today_files || 0) > 0) : [];

  return (
    <div className="max-w-3xl space-y-5" data-testid="storage-console">
      {/* Usage summary + quota bar */}
      <Card className="p-5" data-testid="storage-usage-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--diamond-bg)] text-[var(--diamond-icon)]">
              <HardDrive size={18} />
            </div>
            <div>
              <div className="font-medium text-[var(--ink-navy)]">Object Storage Usage</div>
              <p className="text-xs text-slate-500 mt-1 max-w-lg">
                Total bytes stored across all uploaded files, measured against the 5 GB plan quota.
              </p>
            </div>
          </div>
          <Button
            onClick={() => load(true)}
            disabled={loading || refreshing}
            variant="outline"
            size="sm"
            className="h-8 text-xs shrink-0"
            data-testid="storage-refresh-button"
          >
            <RefreshCw size={13} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>

        {loading && !data ? (
          <div className="mt-6 space-y-3" data-testid="storage-loading">
            <div className="h-4 w-40 animate-pulse rounded bg-slate-100" />
            <div className="h-3 w-full animate-pulse rounded-full bg-slate-100" />
            <div className="h-3 w-52 animate-pulse rounded bg-slate-100" />
          </div>
        ) : error && !data ? (
          <div className="mt-5 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="storage-error">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        ) : data ? (
          <div className="mt-5">
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="text-2xl font-semibold text-[var(--ink-navy)]" data-testid="storage-total-used">
                  {formatBytes(totalBytes)}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  used of {formatBytes(data.quota_bytes)} · {data.total_files.toLocaleString()} files
                </div>
              </div>
              <div className={`text-lg font-semibold ${colors.text}`} data-testid="storage-percent-used">
                {pct.toFixed(2)}%
              </div>
            </div>
            <div className={`mt-3 h-3 w-full overflow-hidden rounded-full ${colors.soft}`} role="progressbar"
              aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} data-testid="storage-quota-bar">
              <div
                className={`h-full rounded-full ${colors.bar} transition-[width] duration-500 ease-out`}
                style={{ width: `${Math.max(pct, pct > 0 ? 1.5 : 0)}%` }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
              <span>0</span>
              <span>{formatBytes(data.quota_bytes)}</span>
            </div>
            <p className="mt-3 text-[11px] text-slate-400" data-testid="storage-last-refreshed">
              Last refreshed: {formatTimestamp(data.generated_at)}
            </p>
          </div>
        ) : null}
      </Card>

      {/* Today's usage — added today, grouped by the same modules as the breakdown */}
      <Card className="p-5" data-testid="storage-today-card">
        <div className="flex items-center justify-between">
          <div className="font-medium text-[var(--ink-navy)]">Today's Usage</div>
          <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold" data-testid="storage-today-date">
            {data?.today_label || '—'}{data?.today_timezone ? ` · ${data.today_timezone}` : ''}
          </span>
        </div>
        {loading && !data ? (
          <div className="mt-4 space-y-2" data-testid="storage-today-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-7 w-full animate-pulse rounded bg-slate-100" />
            ))}
          </div>
        ) : data ? (
          <div className="mt-3">
            <div className="flex items-end justify-between gap-4">
              <div className="text-xl font-semibold text-[var(--ink-navy)]" data-testid="storage-today-total">
                {formatBytes(data.today_bytes || 0)}
              </div>
              <div className="text-xs text-slate-500" data-testid="storage-today-files">
                {(data.today_files || 0).toLocaleString()} {data.today_files === 1 ? 'file' : 'files'} added today
              </div>
            </div>
            {todayModules.length === 0 ? (
              <p className="mt-3 text-xs text-slate-400" data-testid="storage-today-empty">
                No files added today.
              </p>
            ) : (
              <div className="mt-3 overflow-hidden rounded-lg border border-[var(--divider)]" data-testid="storage-today-list">
                {todayModules.map((m) => (
                  <div
                    key={m.key}
                    data-testid={`storage-today-row-${m.key}`}
                    className="flex items-center justify-between gap-3 border-t border-[var(--divider)] px-3 py-2.5 text-sm first:border-t-0"
                  >
                    <span className="font-medium text-[var(--ink-navy)]">{m.name}</span>
                    <span className="flex items-center gap-4 tabular-nums text-slate-500">
                      <span className="text-[11px] font-medium text-[var(--diamond-icon)]" data-testid={`storage-today-avg-${m.key}`}>
                        avg {formatAvgKB(m.today_bytes, m.today_files)}
                      </span>
                      <span className="text-[11px]" data-testid={`storage-today-files-${m.key}`}>
                        {m.today_files.toLocaleString()} {m.today_files === 1 ? 'file' : 'files'}
                      </span>
                      <span className="w-20 text-right font-medium text-[var(--ink-navy)]" data-testid={`storage-today-size-${m.key}`}>
                        {formatBytes(m.today_bytes)}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : error ? (
          <p className="mt-3 text-xs text-slate-400">Today's usage is unavailable until the snapshot loads.</p>
        ) : null}
        <p className="mt-3 text-[11px] text-slate-400">
          Files uploaded since midnight IST, grouped by the same modules as the breakdown below.
        </p>
      </Card>

      {/* Per-module breakdown */}
      <Card className="p-5" data-testid="storage-breakdown-card">
        <div className="flex items-center justify-between">
          <div className="font-medium text-[var(--ink-navy)]">Breakdown by Module</div>
          <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Files · Size</span>
        </div>
        {loading && !data ? (
          <div className="mt-4 space-y-2" data-testid="storage-breakdown-loading">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-8 w-full animate-pulse rounded bg-slate-100" />
            ))}
          </div>
        ) : data ? (
          <div className="mt-3 overflow-hidden rounded-lg border border-[var(--divider)]">
            <table className="w-full text-sm" data-testid="storage-breakdown-table">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 font-semibold">Module</th>
                  <th className="px-3 py-2 font-semibold text-right">Avg Size</th>
                  <th className="px-3 py-2 font-semibold text-right">Files</th>
                  <th className="px-3 py-2 font-semibold text-right">Size</th>
                  <th className="px-3 py-2 font-semibold text-right w-[26%]">Share</th>
                </tr>
              </thead>
              <tbody>
                {data.modules.map((m) => {
                  const share = totalBytes ? (m.bytes / totalBytes) * 100 : 0;
                  const empty = m.files === 0;
                  return (
                    <tr
                      key={m.key}
                      className={`border-t border-[var(--divider)] ${empty ? 'text-slate-400' : 'text-[var(--ink-navy)]'}`}
                      data-testid={`storage-row-${m.key}`}
                    >
                      <td className="px-3 py-2.5 font-medium">{m.name}</td>
                      <td className={`px-3 py-2.5 text-right tabular-nums ${empty ? 'text-slate-400' : 'text-[var(--diamond-icon)] font-medium'}`} data-testid={`storage-avg-${m.key}`}>
                        {formatAvgKB(m.bytes, m.files)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums" data-testid={`storage-files-${m.key}`}>
                        {m.files.toLocaleString()}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums" data-testid={`storage-size-${m.key}`}>
                        {formatBytes(m.bytes)}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-2">
                          <div className="h-1.5 w-full max-w-[110px] overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-[var(--diamond-icon)]"
                              style={{ width: `${share > 0 && share < 1.5 ? 1.5 : share}%` }}
                            />
                          </div>
                          <span className="w-10 text-right text-[11px] tabular-nums text-slate-500">
                            {share.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[var(--divider)] bg-slate-50 font-semibold text-[var(--ink-navy)]">
                  <td className="px-3 py-2.5">Total</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[var(--diamond-icon)]" data-testid="storage-total-avg">
                    {formatAvgKB(totalBytes, data.total_files)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums" data-testid="storage-total-files">
                    {(data.total_files || 0).toLocaleString()}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums" data-testid="storage-total-size">
                    {formatBytes(totalBytes)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-[11px] text-slate-500">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : error ? (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            <span className="flex items-center gap-2"><AlertTriangle size={14} /> {error}</span>
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => load(true)} data-testid="storage-retry-button">
              Retry
            </Button>
          </div>
        ) : null}
        <p className="mt-3 text-[11px] text-slate-400">
          Modules match the ops usage report. "Repairs / Customer Jewellery" share one storage scope.
          Tiny sizes (a few bytes) in some modules are leftover migration test files.
        </p>
      </Card>
    </div>
  );
}
